import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';

import { WorkflowService, TaskChartData } from '@services/workflow.service';
import { AgentsService } from '@services/agents.service';
import { WorkflowJobsService } from '@services/workflow-jobs.service';
import { WorkflowJobRunsService } from '@services/workflow-job-runs.service';
import { Tables } from '@models/database.types';
import { Agent } from '@models/agent.types';

// Import widgets
import { TasksStatsWidget } from './widgets/tasks-stats-widget/tasks-stats-widget';
import { AgentsStatsWidget } from './widgets/agents-stats-widget/agents-stats-widget';
import { ExecutionsStatsWidget } from './widgets/executions-stats-widget/executions-stats-widget';
import { SuccessRateWidget } from './widgets/success-rate-widget/success-rate-widget';
import { RunningJobsWidget } from './widgets/running-jobs-widget/running-jobs-widget';
import { TasksChartWidget } from './widgets/tasks-chart-widget/tasks-chart-widget';
import { AgentStatusWidget } from './widgets/agent-status-widget/agent-status-widget';

interface DashboardStats {
  totalTasks: number;
  totalJobExecutions: number;
  activeAgents: number;
  totalRuns: number;
  completedRuns: number;
  failedRuns: number;
  runningJobs: number;
  avgRunDurationSeconds: number;
  lastExecutionTime: Date | null;
  todayStats: { completed: number; failed: number };
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    TasksStatsWidget,
    AgentsStatsWidget,
    ExecutionsStatsWidget,
    SuccessRateWidget,
    RunningJobsWidget,
    TasksChartWidget,
    AgentStatusWidget
  ],
  templateUrl: './dashboard.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Dashboard implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  loading = true;
  stats: DashboardStats = {
    totalTasks: 0,
    totalJobExecutions: 0,
    activeAgents: 0,
    totalRuns: 0,
    completedRuns: 0,
    failedRuns: 0,
    runningJobs: 0,
    avgRunDurationSeconds: 0,
    lastExecutionTime: null,
    todayStats: { completed: 0, failed: 0 }
  };

  recentTasks: Tables<'workflows'>[] = [];
  agents: Agent[] = [];
  tasksChartData: TaskChartData[] = [];

  constructor(
    private workflowService: WorkflowService,
    private agentsService: AgentsService,
    private workflowJobsService: WorkflowJobsService,
    private workflowJobRunsService: WorkflowJobRunsService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.loadDashboardData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadDashboardData() {
    this.loading = true;
    this.cdr.markForCheck();

    // Load all data in parallel
    const workflows$ = this.workflowService.getWorkflows();
    const agents$ = this.agentsService.list();
    const jobs$ = this.workflowJobsService.list();
    const avgDuration$ = this.workflowJobRunsService.getAverageDuration();
    const tasksChart$ = this.workflowJobRunsService.getExecutionsByDay();
    const lastExecutionTime$ = this.workflowJobRunsService.getLastExecutionTime();

    let workflowsLoaded = false;
    let agentsLoaded = false;
    let jobsLoaded = false;
    let avgDurationLoaded = false;
    let tasksChartLoaded = false;
    let lastExecutionTimeLoaded = false;
    let todayStatsLoaded = false;

    const checkComplete = () => {
      if (workflowsLoaded && agentsLoaded && jobsLoaded && avgDurationLoaded && tasksChartLoaded && lastExecutionTimeLoaded && todayStatsLoaded) {
        this.loading = false;
        this.cdr.markForCheck();
      }
    };

    // Load workflows
    workflows$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (workflows) => {
        console.log('Dashboard: Workflows loaded', workflows.length);
        this.stats.totalTasks = workflows.length;
        this.recentTasks = workflows.slice(0, 5); // Show last 5 tasks
        workflowsLoaded = true;
        checkComplete();
      },
      error: (error) => {
        console.error('Failed to load workflows:', error);
        workflowsLoaded = true;
        checkComplete();
      }
    });

    // Load agents
    agents$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (agents) => {
        this.stats.activeAgents = agents.length;
        this.agents = agents;
        agentsLoaded = true;
        checkComplete();
      },
      error: (error) => {
        console.error('Failed to load agents:', error);
        agentsLoaded = true;
        checkComplete();
      }
    });

    // Load jobs
    jobs$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        const jobs = result.data;
        this.stats.totalJobExecutions = result.count;
        this.stats.runningJobs = jobs.filter(job => job.latest_status === 'pending').length;

        // Calculate completed and failed runs
        const completedJobs = jobs.filter(job => job.latest_status === 'completed');
        const failedJobs = jobs.filter(job => job.latest_status === 'failed');

        this.stats.completedRuns = completedJobs.length;
        this.stats.failedRuns = failedJobs.length;
        this.stats.totalRuns = completedJobs.length + failedJobs.length;

        jobsLoaded = true;
        checkComplete();
      },
      error: (error) => {
        console.error('Failed to load jobs:', error);
        jobsLoaded = true;
        checkComplete();
      }
    });

    // Load average duration
    avgDuration$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (avgDuration) => {
        this.stats.avgRunDurationSeconds = avgDuration;
        avgDurationLoaded = true;
        checkComplete();
      },
      error: (error) => {
        console.error('Failed to load average duration:', error);
        avgDurationLoaded = true;
        checkComplete();
      }
    });

    // Load tasks chart data
    tasksChart$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (chartData) => {
        this.tasksChartData = chartData;
        tasksChartLoaded = true;
        checkComplete();
      },
      error: (error) => {
        console.error('Failed to load tasks chart data:', error);
        tasksChartLoaded = true;
        checkComplete();
      }
    });

    // Load last execution time
    lastExecutionTime$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (lastExecutionTime) => {
        this.stats.lastExecutionTime = lastExecutionTime;
        lastExecutionTimeLoaded = true;
        checkComplete();
      },
      error: (error) => {
        console.error('Failed to load last execution time:', error);
        lastExecutionTimeLoaded = true;
        checkComplete();
      }
    });

    // Load today stats
    this.workflowJobRunsService.getTodayStats().pipe(takeUntil(this.destroy$)).subscribe({
      next: (stats) => {
        this.stats.todayStats = stats;
        todayStatsLoaded = true;
        checkComplete();
      },
      error: (error) => {
        console.error('Failed to load today stats:', error);
        todayStatsLoaded = true;
        checkComplete();
      }
    });
  }

}